import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  api,
  type UnifiedAccountSummary,
  type UnifiedAccountsSnapshot,
  type UnifiedConnectionSummary,
  type UnifiedExchangePayload,
  type UnifiedPositionsResponse,
  type UnifiedTransactionsResponse
} from '../../../api';
import type { FeatureContext } from '../../types';
import './unified-accounts.css';

declare global {
  interface Window {
    Plaid?: {
      create(config: PlaidCreateConfig): PlaidLinkHandler;
    };
  }
}

interface PlaidInstitutionMetadata {
  institution_id?: string | null;
  name?: string | null;
  url?: string | null;
  logo?: string | null;
  primary_color?: string | null;
}

interface PlaidAccountMetadata {
  id: string;
  name?: string | null;
  mask?: string | null;
  type?: string | null;
  subtype?: string | null;
}

interface PlaidSuccessMetadata {
  institution?: PlaidInstitutionMetadata | null;
  accounts: PlaidAccountMetadata[];
}

interface PlaidExitError {
  error_code?: string;
  error_message?: string;
}

interface PlaidLinkHandler {
  open: () => void;
  exit?: (options?: { force?: boolean }) => void;
  destroy: () => void;
}

interface PlaidCreateConfig {
  token: string;
  onSuccess: (publicToken: string, metadata: PlaidSuccessMetadata) => void;
  onExit?: (error: PlaidExitError | null) => void;
  receivedRedirectUri?: string;
}

let plaidScriptPromise: Promise<void> | null = null;

async function ensurePlaidScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.Plaid) {
    return;
  }

  if (plaidScriptPromise) {
    return plaidScriptPromise;
  }

  plaidScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-plaid-link]');
    if (existing) {
      if (existing.getAttribute('data-plaid-loaded') === 'true' && window.Plaid) {
        resolve();
        return;
      }

      const handleLoad = () => {
        existing.setAttribute('data-plaid-loaded', 'true');
        resolve();
      };
      const handleError = () => {
        plaidScriptPromise = null;
        reject(new Error('Failed to load Plaid Link.'));
      };
      existing.addEventListener('load', handleLoad, { once: true });
      existing.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    script.async = true;
    script.setAttribute('data-plaid-link', 'true');
    script.addEventListener('load', () => {
      script.setAttribute('data-plaid-loaded', 'true');
      resolve();
    });
    script.addEventListener('error', () => {
      plaidScriptPromise = null;
      reject(new Error('Failed to load Plaid Link.'));
    });
    document.body.appendChild(script);
  });

  return plaidScriptPromise;
}

function formatCurrency(value: number | null | undefined, currency?: string | null): string {
  if (value === null || value === undefined) {
    return '—';
  }

  try {
    const formatter = new Intl.NumberFormat(
      undefined,
      {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 2
      }
    );
    return formatter.format(value);
  } catch {
    return `${currency ?? ''} ${value.toFixed(2)}`.trim();
  }
}

const ISO_TIMEZONE_REGEX = /([zZ]|[+-]\d{2}:?\d{2})$/;

function coerceDate(value: string | null | undefined, referenceMs: number = Date.now()): Date | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const candidates: number[] = [];
  const direct = Date.parse(trimmed);
  if (!Number.isNaN(direct)) {
    candidates.push(direct);
  }

  const hasTimezone = ISO_TIMEZONE_REGEX.test(trimmed);
  if (!hasTimezone) {
    const utcGuess = Date.parse(trimmed.endsWith('Z') ? trimmed : `${trimmed}Z`);
    if (!Number.isNaN(utcGuess)) {
      candidates.push(utcGuess);
    }
  } else if (/[zZ]$/.test(trimmed)) {
    const withoutZ = Date.parse(trimmed.slice(0, -1));
    if (!Number.isNaN(withoutZ)) {
      candidates.push(withoutZ);
    }
  }

  if (!candidates.length) {
    return null;
  }

  let best = candidates[0];
  let smallestDelta = Math.abs(referenceMs - best);
  for (let i = 1; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    const delta = Math.abs(referenceMs - candidate);
    if (delta < smallestDelta) {
      best = candidate;
      smallestDelta = delta;
    }
  }

  const parsed = new Date(best);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(value: string | null | undefined): string {
  const parsed = coerceDate(value);
  if (!parsed) {
    return 'Never';
  }
  return parsed.toLocaleString();
}

function formatRelativeTime(value: string | null | undefined, now: number = Date.now()): string {
  const parsed = coerceDate(value, now);
  if (!parsed) {
    return 'Never synced';
  }

  const diffMs = now - parsed.getTime();
  if (diffMs <= 0 || diffMs < 60_000) {
    return 'Just now';
  }

  const format = (amount: number, unit: string) => `${amount} ${unit}${amount === 1 ? '' : 's'} ago`;

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) {
    return format(minutes, 'minute');
  }

  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 24) {
    return format(hours, 'hour');
  }

  const days = Math.floor(diffMs / 86_400_000);
  if (days < 30) {
    return format(days, 'day');
  }

  const months = Math.floor(diffMs / 2_592_000_000);
  if (months < 12) {
    return format(months, 'month');
  }

  const years = Math.floor(diffMs / 31_536_000_000);
  return format(years, 'year');
}

function isInvestmentAccount(account: UnifiedAccountSummary | undefined | null): boolean {
  if (!account) {
    return false;
  }

  const type = account.accountType.toLowerCase();
  return type === 'investment' || type === 'brokerage' || type === '401k';
}

function isDebtAccountType(accountType: string | undefined | null): boolean {
  const lowered = (accountType ?? '').toLowerCase();
  return (
    lowered.includes('credit') ||
    lowered.includes('loan') ||
    lowered.includes('mortgage') ||
    lowered.includes('liability') ||
    lowered.includes('debt') ||
    lowered.includes('line of credit')
  );
}

function normalizedAccountBalance(account: UnifiedAccountSummary | undefined | null): number | null {
  if (!account || account.currentBalance === undefined || account.currentBalance === null) {
    return null;
  }
  const raw = account.currentBalance;
  return isDebtAccountType(account.accountType) ? -Math.abs(raw) : raw;
}

export function UnifiedAccountsFeature({ isAuthenticated }: FeatureContext) {
  const [snapshot, setSnapshot] = useState<UnifiedAccountsSnapshot | null>(null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [transactionsState, setTransactionsState] = useState<{
    loading: boolean;
    error: string | null;
    data: UnifiedTransactionsResponse | null;
  }>({
    loading: false,
    error: null,
    data: null
  });
  const [positionsState, setPositionsState] = useState<{
    loading: boolean;
    error: string | null;
    data: UnifiedPositionsResponse | null;
  }>({
    loading: false,
    error: null,
    data: null
  });
  const [linking, setLinking] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [refreshingConnectionId, setRefreshingConnectionId] = useState<string | null>(null);
  const linkHandlerRef = useRef<PlaidLinkHandler | null>(null);
  const [pendingOauth, setPendingOauth] = useState<{ stateId: string; redirectUri: string } | null>(null);
  const [removingConnectionId, setRemovingConnectionId] = useState<string | null>(null);
  const [removingAccountId, setRemovingAccountId] = useState<string | null>(null);
  const [relativeNow, setRelativeNow] = useState(() => Date.now());
  const [revealedAccountIds, setRevealedAccountIds] = useState<Record<string, boolean>>({});

  const loadSnapshot = useCallback(async () => {
    if (!isAuthenticated) {
      setSnapshot(null);
      setSelectedAccountId(null);
      setSnapshotError('Sign in to manage your financial institutions.');
      return;
    }

    setLoadingSnapshot(true);
    setSnapshotError(null);

    try {
      const response = await api.getUnifiedAccounts();
      setSnapshot(response);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load accounts.';
      setSnapshotError(message);
      setSnapshot(null);
    } finally {
      setLoadingSnapshot(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void loadSnapshot();
  }, [loadSnapshot]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setRelativeNow(Date.now());
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    return () => {
      linkHandlerRef.current?.destroy();
      linkHandlerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!snapshot || snapshot.accounts.length === 0) {
      setSelectedAccountId(null);
      return;
    }

    if (!selectedAccountId || !snapshot.accounts.some((account) => account.id === selectedAccountId)) {
      setSelectedAccountId(snapshot.accounts[0].id);
    }
  }, [snapshot, selectedAccountId]);

  useEffect(() => {
    if (!selectedAccountId || !snapshot) {
      setTransactionsState({ loading: false, error: null, data: null });
      setPositionsState({ loading: false, error: null, data: null });
      return;
    }

    let cancelled = false;

    const loadTransactions = async () => {
      setTransactionsState((state) => ({ ...state, loading: true, error: null }));
      try {
        const response = await api.getUnifiedAccountTransactions(selectedAccountId, { limit: 100 });
        if (!cancelled) {
          setTransactionsState({ loading: false, error: null, data: response });
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Unable to load transactions.';
          setTransactionsState({ loading: false, error: message, data: null });
        }
      }
    };

    const loadPositions = async () => {
      const account = snapshot.accounts.find((acct) => acct.id === selectedAccountId);
      if (!isInvestmentAccount(account)) {
        setPositionsState({ loading: false, error: null, data: null });
        return;
      }

      setPositionsState((state) => ({ ...state, loading: true, error: null }));
      try {
        const response = await api.getUnifiedAccountPositions(selectedAccountId);
        if (!cancelled) {
          setPositionsState({ loading: false, error: null, data: response });
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Unable to load positions.';
          setPositionsState({ loading: false, error: message, data: null });
        }
      }
    };

    void loadTransactions();
    void loadPositions();

    return () => {
      cancelled = true;
    };
  }, [selectedAccountId, snapshot]);

  const selectedAccount = useMemo<UnifiedAccountSummary | undefined>(() => {
    if (!snapshot || !selectedAccountId) {
      return undefined;
    }
    return snapshot.accounts.find((account) => account.id === selectedAccountId);
  }, [selectedAccountId, snapshot]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const currentUrl = new URL(window.location.href);
    if (currentUrl.pathname === '/plaid/oauth-return') {
      const stateId = currentUrl.searchParams.get('plaid_oauth_state_id');
      if (stateId) {
        setPendingOauth({ stateId, redirectUri: currentUrl.href });
      }

      window.history.replaceState(null, '', '/');
    }
  }, []);

  const handleLinkInstitution = useCallback(
    async (options?: { connectionId?: string; oauthStateId?: string; receivedRedirectUri?: string }) => {
    if (!isAuthenticated) {
      setStatusMessage('Sign in to link a financial institution.');
      return;
    }

    setLinking(true);
    setStatusMessage(null);

    try {
      const linkResponse = await api.createUnifiedAccountsLinkToken({
        connectionId: options?.connectionId,
        oauthStateId: options?.oauthStateId
      });
      await ensurePlaidScript();

      if (!window.Plaid) {
        throw new Error('Plaid Link is unavailable.');
      }

      const handler = window.Plaid.create({
        token: linkResponse.linkToken,
        receivedRedirectUri: options?.receivedRedirectUri,
        onSuccess: async (publicToken, metadata) => {
          setLinking(false);
          setStatusMessage('Link successful. Syncing accounts...');
          try {
            const exchangePayload: UnifiedExchangePayload = {
              publicToken,
              institution: metadata.institution
                ? {
                    institutionId: metadata.institution.institution_id ?? null,
                    name: metadata.institution.name ?? null,
                    url: metadata.institution.url ?? null,
                    logoUrl: metadata.institution.logo ?? null,
                    primaryColor: metadata.institution.primary_color ?? null
                  }
                : undefined,
              accounts: metadata.accounts?.map((account) => ({
                accountId: account.id,
                name: account.name ?? null,
                mask: account.mask ?? null,
                type: account.type ?? null,
                subtype: account.subtype ?? null
              }))
            };

            await api.exchangeUnifiedAccountsPublicToken(exchangePayload);
            await loadSnapshot();
            setStatusMessage('Institution linked successfully.');
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to save the new connection.';
            setStatusMessage(message);
          }
        },
        onExit: (error) => {
          setLinking(false);
          if (error?.error_message) {
            setStatusMessage(error.error_message);
          }
        }
      });

      handler.open();
      linkHandlerRef.current?.destroy();
      linkHandlerRef.current = handler;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to launch Plaid Link.';
      setStatusMessage(message);
      setLinking(false);
    }
    },
    [isAuthenticated, loadSnapshot]
  );

  useEffect(() => {
    if (!pendingOauth) {
      return;
    }

    setStatusMessage('Completing Plaid OAuth…');
    void (async () => {
      try {
        await handleLinkInstitution({
          oauthStateId: pendingOauth.stateId,
          receivedRedirectUri: pendingOauth.redirectUri,
        });
      } finally {
        setPendingOauth(null);
      }
    })();
  }, [pendingOauth, handleLinkInstitution]);

  const handleRefreshConnection = useCallback(
    async (connectionId: string) => {
      setRefreshingConnectionId(connectionId);
      setStatusMessage(null);

      try {
        await api.refreshUnifiedConnection(connectionId);
        setStatusMessage('Refresh enqueued. Latest balances will show up shortly.');
        await loadSnapshot();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to refresh this connection.';
        setStatusMessage(message);
      } finally {
        setRefreshingConnectionId(null);
      }
    },
    [loadSnapshot]
  );

  const handleRemoveConnection = useCallback(
    async (connectionId: string) => {
      if (!window.confirm('Remove this institution and all associated accounts?')) {
        return;
      }

      setRemovingConnectionId(connectionId);
      setStatusMessage(null);

      try {
        await api.deleteUnifiedConnection(connectionId);
        setStatusMessage('Institution removed.');
        await loadSnapshot();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to remove this connection.';
        setStatusMessage(message);
      } finally {
        setRemovingConnectionId(null);
      }
    },
    [loadSnapshot]
  );

  const handleRemoveAccount = useCallback(
    async (accountId: string) => {
      if (!window.confirm('Remove this account from Project Hermes?')) {
        return;
      }

      setRemovingAccountId(accountId);
      setStatusMessage(null);

      try {
        await api.deleteUnifiedAccount(accountId);
        setStatusMessage('Account removed.');
        await loadSnapshot();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to remove this account.';
        setStatusMessage(message);
      } finally {
        setRemovingAccountId(null);
      }
    },
    [loadSnapshot]
  );

  const handleToggleAccountNumber = useCallback((accountId: string) => {
    setRevealedAccountIds((prev) => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  }, []);

  const renderConnections = () => {
    if (!snapshot?.connections.length) {
      return <p className="unified-accounts__empty">No institutions linked yet.</p>;
    }

    return snapshot.connections.map((connection) => {
      const normalizedStatus = (connection.status ?? 'unknown').toLowerCase();
      const statusTone = normalizedStatus === 'healthy' ? 'healthy' : 'error';
      const statusLabel = normalizedStatus
        ? normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1)
        : 'Unknown';

      return (
        <div key={connection.id} className="unified-accounts__connection">
          <div className="unified-accounts__connection-info">
            <div className="unified-accounts__connection-name-row">
              <div className="unified-accounts__connection-name">
                {connection.institutionName ?? 'Institution'}
              </div>
              <span
                className={`unified-accounts__status-pill unified-accounts__status-pill--${statusTone}`}
              >
                {statusLabel}
              </span>
            </div>
            <div className="unified-accounts__connection-meta">
              <span className="unified-accounts__connection-sync-time">
                Last sync {formatDate(connection.lastSyncedAt)}
              </span>
              <span className="unified-accounts__connection-sync-relative">
                Synced {formatRelativeTime(connection.lastSyncedAt, relativeNow)}
              </span>
            </div>
          </div>
          <div className="unified-accounts__connection-actions">
            <button
              type="button"
              className="unified-accounts__pill-button unified-accounts__pill-button--ghost"
              onClick={() => handleRefreshConnection(connection.id)}
              disabled={refreshingConnectionId === connection.id}
            >
              {refreshingConnectionId === connection.id ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              type="button"
              className="unified-accounts__pill-button unified-accounts__pill-button--danger"
              onClick={() => handleRemoveConnection(connection.id)}
              disabled={removingConnectionId === connection.id}
            >
              {removingConnectionId === connection.id ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </div>
      );
    });
  };

  const renderAccounts = () => {
    if (!snapshot?.accounts.length) {
      return (
        <p className="unified-accounts__empty">
          Link your first institution to see balances and holdings here.
        </p>
      );
    }

    return snapshot.accounts.map((account) => {
      const isSelected = selectedAccountId === account.id;
      const classes = ['unified-accounts__card'];
      if (isSelected) {
        classes.push('unified-accounts__card--selected');
      }

      const balanceValue = normalizedAccountBalance(account);
      const formattedAccountValue = formatCurrency(balanceValue, account.currency);

      return (
        <button
          key={account.id}
          type="button"
          className={classes.join(' ')}
          onClick={() => setSelectedAccountId(account.id)}
        >
          <div className="unified-accounts__card-header">
              <div className="unified-accounts__card-info">
                <div className="unified-accounts__card-name">{account.institutionName ?? '—'}</div>
                <div className="unified-accounts__account-label">{account.name}</div>
                <div className="unified-accounts__account-row">
                  <span className="unified-accounts__account-type">
                    {account.accountType}
                    {account.accountSubtype ? ` · ${account.accountSubtype}` : ''}
                  </span>
                  {account.mask ? <span className="unified-accounts__account-mask">••••{account.mask}</span> : null}
                </div>
              </div>
            <div className="unified-accounts__card-balance">
              <span className="unified-accounts__card-balance-label">Account value</span>
              <span className="unified-accounts__card-balance-amount">{formattedAccountValue}</span>
            </div>
          </div>
          <div className="unified-accounts__card-footer">
            <div className="unified-accounts__card-updated-block">
              <span className="unified-accounts__card-updated">
                Updated {formatDate(account.lastSyncedAt)}
              </span>
              <span className="unified-accounts__card-updated-relative">
                Last updated {formatRelativeTime(account.lastSyncedAt, relativeNow)}
              </span>
            </div>
            <button
              type="button"
              className="unified-accounts__pill-button unified-accounts__pill-button--danger"
              onClick={(event) => {
                event.stopPropagation();
                void handleRemoveAccount(account.id);
              }}
              disabled={removingAccountId === account.id}
            >
              {removingAccountId === account.id ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </button>
      );
    });
  };

  const renderTransactions = () => {
    if (!selectedAccount) {
      return <p className="unified-accounts__empty">Select an account to view activity.</p>;
    }

    if (transactionsState.loading) {
      return <p className="unified-accounts__muted">Loading transactions…</p>;
    }

    if (transactionsState.error) {
      return (
        <div className="unified-accounts__callout unified-accounts__callout--error">
          {transactionsState.error}
        </div>
      );
    }

    const transactions = transactionsState.data?.transactions ?? [];
    if (!transactions.length) {
      return <p className="unified-accounts__muted">No recent transactions reported.</p>;
    }

    return (
      <div className="unified-accounts__table-wrapper">
        <table className="unified-accounts__table">
          <thead>
            <tr>
              <th scope="col">Merchant</th>
              <th scope="col">Category</th>
              <th scope="col">MCC</th>
              <th scope="col" className="unified-accounts__amount">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((transaction) => (
              <tr key={transaction.id}>
                <td>
                  <div className="unified-accounts__transaction-merchant">
                    <strong>{transaction.merchantName ?? transaction.description ?? '—'}</strong>
                    <span>{formatDate(transaction.postedAt)}</span>
                  </div>
                </td>
                <td>{transaction.category ?? '—'}</td>
                <td>{transaction.merchantCategoryCode ?? '—'}</td>
                <td className="unified-accounts__amount">
                  {formatCurrency(transaction.amount, transaction.isoCurrencyCode ?? selectedAccount.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPositions = () => {
    if (!selectedAccount || !isInvestmentAccount(selectedAccount)) {
      return null;
    }

    if (positionsState.loading) {
      return <p className="unified-accounts__muted">Loading holdings…</p>;
    }

    if (positionsState.error) {
      return <p className="unified-accounts__error">{positionsState.error}</p>;
    }

    const positions = positionsState.data?.positions ?? [];
    if (!positions.length) {
      return <p className="unified-accounts__muted">No holdings reported for this account.</p>;
    }

    return (
      <div className="unified-accounts__table-wrapper">
        <table className="unified-accounts__table">
          <thead>
            <tr>
              <th scope="col">Symbol</th>
              <th scope="col">Quantity</th>
              <th scope="col">Price</th>
              <th scope="col">Market Value</th>
              <th scope="col">Unrealized Gain</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((position) => (
              <tr key={position.id}>
                <td>
                  <div className="unified-accounts__symbol">
                    <strong>{position.symbol ?? '—'}</strong>
                    <span>{position.name ?? '—'}</span>
                  </div>
                </td>
                <td>{position.quantity?.toFixed(4) ?? '—'}</td>
                <td>{formatCurrency(position.price, selectedAccount.currency)}</td>
                <td>{formatCurrency(position.institutionValue, selectedAccount.currency)}</td>
                <td>{formatCurrency(position.unrealizedGain, selectedAccount.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const totals = snapshot ?? null;
  const connectionCount = snapshot?.connections.length ?? 0;
  const accountCount = snapshot?.accounts.length ?? 0;

  return (
    <div className="unified-accounts">
      <div className="unified-accounts__hero">
        <div>
          <p className="unified-accounts__eyebrow">Finance</p>
          <h2 className="unified-accounts__title">Unified Financial Accounts</h2>
          <p className="unified-accounts__lead">
            Link banks and brokerages via Plaid to view balances, holdings, and transactions in one
            workspace.
          </p>
        </div>
        <div className="unified-accounts__actions">
          <button
            type="button"
            className="unified-accounts__button unified-accounts__button--primary"
            onClick={() => {
              void handleLinkInstitution();
            }}
            disabled={!isAuthenticated || linking}
          >
            {linking ? 'Connecting…' : 'Link Institution'}
          </button>
          <p className="unified-accounts__timestamp">
            Last updated {formatDate(totals?.lastSyncedAt)}
          </p>
        </div>
      </div>

      <div className="unified-accounts__metrics">
        <div className="unified-accounts__metric">
          <p className="unified-accounts__metric-label">Net Worth</p>
          <p className="unified-accounts__metric-value">
            {formatCurrency(totals?.netWorth ?? null)}
          </p>
        </div>
        <div className="unified-accounts__metric">
          <p className="unified-accounts__metric-label">Cash</p>
          <p className="unified-accounts__metric-value">
            {formatCurrency(totals?.cashTotal ?? null)}
          </p>
        </div>
        <div className="unified-accounts__metric">
          <p className="unified-accounts__metric-label">Investments</p>
          <p className="unified-accounts__metric-value">
            {formatCurrency(totals?.investmentTotal ?? null)}
          </p>
        </div>
        <div className="unified-accounts__metric">
          <p className="unified-accounts__metric-label">Debt</p>
          <p className="unified-accounts__metric-value">
            {formatCurrency(totals?.debtTotal ?? null)}
          </p>
        </div>
      </div>

      {statusMessage && <div className="unified-accounts__status">{statusMessage}</div>}
      {snapshotError && <div className="unified-accounts__error">{snapshotError}</div>}
      {loadingSnapshot && <p className="unified-accounts__muted">Loading accounts…</p>}

      {!loadingSnapshot && (
        <div className="unified-accounts__grid">
          <div className="unified-accounts__column">
            <section className="unified-accounts__panel">
              <div className="unified-accounts__panel-heading">
                <h3>Institutions</h3>
                <span className="unified-accounts__count-badge">{connectionCount}</span>
              </div>
              {renderConnections()}
            </section>
            <section className="unified-accounts__panel">
              <div className="unified-accounts__panel-heading">
                <h3>Accounts</h3>
                <span className="unified-accounts__count-badge">{accountCount}</span>
              </div>
              <div className="unified-accounts__cards">{renderAccounts()}</div>
            </section>
          </div>
          <section className="unified-accounts__panel unified-accounts__details">
            <div className="unified-accounts__details-header">
              <div>
                <h3>{selectedAccount?.name ?? 'Account activity'}</h3>
                {selectedAccount && (
                  <div className="unified-accounts__account-number">
                    <span className="unified-accounts__account-number-label">Account number</span>
                    <span className="unified-accounts__account-number-value">
                      {(() => {
                        const isRevealed = Boolean(revealedAccountIds[selectedAccount.id]);
                        if (isRevealed && selectedAccount.accountNumber) {
                          return selectedAccount.accountNumber;
                        }
                        return selectedAccount.mask ? `•••• ${selectedAccount.mask}` : '—';
                      })()}
                    </span>
                    {selectedAccount.accountNumber ? (
                      <button
                        type="button"
                        className="unified-accounts__pill-button unified-accounts__pill-button--ghost unified-accounts__account-number-toggle"
                        onClick={() => handleToggleAccountNumber(selectedAccount.id)}
                      >
                        {revealedAccountIds[selectedAccount.id] ? 'Hide' : 'Show'}
                      </button>
                    ) : null}
                  </div>
                )}
                {selectedAccount && (
                  <p className="unified-accounts__muted">
                    {selectedAccount.institutionName ?? '—'} · {selectedAccount.accountType}
                    {selectedAccount.accountSubtype ? ` · ${selectedAccount.accountSubtype}` : ''}
                  </p>
                )}
                {selectedAccount &&
                  (() => {
                    const normalizedBalance = normalizedAccountBalance(selectedAccount);
                    const isDebt = isDebtAccountType(selectedAccount.accountType);
                    const balanceLabel = isDebt ? 'Outstanding balance' : 'Current balance';
                    return (
                      <>
                        <div className="unified-accounts__details-balance">
                          <span className="unified-accounts__details-balance-label">{balanceLabel}</span>
                          <span className="unified-accounts__details-balance-amount">
                            {formatCurrency(normalizedBalance, selectedAccount.currency)}
                          </span>
                        </div>
                        {selectedAccount.availableBalance !== undefined &&
                          selectedAccount.availableBalance !== null &&
                          !isDebt &&
                          selectedAccount.accountType === 'cash' && (
                            <p className="unified-accounts__details-available">
                              Available{' '}
                              <strong>
                                {formatCurrency(
                                  selectedAccount.availableBalance,
                                  selectedAccount.currency
                                )}
                              </strong>
                            </p>
                          )}
                      </>
                    );
                  })()}
              </div>
            </div>
            <div className="unified-accounts__details-body">
              <h4>Recent transactions</h4>
              {renderTransactions()}
              {isInvestmentAccount(selectedAccount) && (
                <>
                  <h4>Holdings</h4>
                  {renderPositions()}
                </>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
